import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { findNode } from '../../utils/treeUtils';
import { TreeNode } from '../../types/types';

interface UseModalHandlersProps {
    clickedOn: string;
    setClickedOn: (val: string) => void;
    navigationTargetKey: string | null;
    setNavigationTargetKey: (val: string | null) => void;
    setIsModalVisible: (val: boolean) => void;
    handleUnsavedChanges: (val: boolean) => void;
    hasUnsavedChanges: boolean;
    setSelectedKey: (val: string | null) => void;
    setSelectedPageName: (val: string) => void;
    setSelectedView: (val: 'editor' | 'pageview') => void;
    setIsCreateDrawerVisible: (val: boolean) => void;
    setIsPublishDrawerVisible: (val: boolean) => void;
    setDrawerOpen: (val: boolean) => void;
    setOpenEditModal: (val: boolean) => void;
    setIsStoresListVisible: (val: boolean) => void;
    data: TreeNode[];
    currentPageVersionGuid: string;
}

export const useModalHandlers = ({
    clickedOn,
    setClickedOn,
    navigationTargetKey,
    setNavigationTargetKey,
    setIsModalVisible,
    handleUnsavedChanges,
    hasUnsavedChanges,
    setSelectedKey,
    setSelectedPageName,
    setSelectedView,
    setIsCreateDrawerVisible,
    setIsPublishDrawerVisible,
    setDrawerOpen,
    setOpenEditModal,
    setIsStoresListVisible,
    data,
    currentPageVersionGuid,
}: UseModalHandlersProps) => {
    const navigate = useNavigate();

    const handleOkModal = useCallback(() => {
        handleUnsavedChanges(false);

        switch (clickedOn) {
            case 'pageversionchange':
                navigate(`/kioskpageversion/${navigationTargetKey}`);
                break;
            case 'page':
                setSelectedKey(navigationTargetKey);
                if (navigationTargetKey) {
                    const selectedNode = findNode(data, navigationTargetKey);
                    if (selectedNode) {
                        setSelectedPageName(selectedNode.title);
                    }
                }
                setSelectedView('editor');
                break;
            case 'createnewpageversion':
                setIsCreateDrawerVisible(true);
                break;
            case 'publish':
                setIsPublishDrawerVisible(true);
                break;
            case 'duplicate':
                setDrawerOpen(true);
                break;
            case 'simulator':
                navigate(`/simulator/${currentPageVersionGuid}`);
                break;
        }

        setClickedOn('');
        setIsModalVisible(false);
        setNavigationTargetKey(null);
    }, [
        clickedOn,
        navigationTargetKey,
        data,
        currentPageVersionGuid,
        navigate,
        handleUnsavedChanges,
        setClickedOn,
        setDrawerOpen,
        setIsCreateDrawerVisible,
        setIsModalVisible,
        setNavigationTargetKey,
        setIsPublishDrawerVisible,
        setSelectedKey,
        setSelectedPageName,
        setSelectedView,
    ]);

    const handleCancelModal = useCallback(() => {
        setIsModalVisible(false);
        setNavigationTargetKey(null);
    }, [setIsModalVisible, setNavigationTargetKey]);

    const handleConditionalAction = useCallback(
        (key: string, fallback: () => void) => {
            if (hasUnsavedChanges) {
                setClickedOn(key);
                setIsModalVisible(true);
            } else {
                fallback();
            }
        },
        [hasUnsavedChanges, setClickedOn, setIsModalVisible]
    );

    const handleCreateNewVersionClick = () =>
        handleConditionalAction('createnewpageversion', () => setIsCreateDrawerVisible(true));

    const handlePublishPageVersionClick = () =>
        handleConditionalAction('publish', () => setIsPublishDrawerVisible(true));

    const handleSimulatorClick = () =>
        handleConditionalAction('simulator', () => navigate(`/simulator/${currentPageVersionGuid}`));

    const handleDuplicateClick = () =>
        handleConditionalAction('duplicate', () => setDrawerOpen(true));

    const handleEditPageVersionClick = () => setOpenEditModal(true);

    const handleShowStoresClick = () => setIsStoresListVisible(true);

    return {
        handleOkModal,
        handleCancelModal,
        handleCreateNewVersionClick,
        handlePublishPageVersionClick,
        handleSimulatorClick,
        handleDuplicateClick,
        handleEditPageVersionClick,
        handleShowStoresClick,
    };
};